import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, extractError, fetchAllListResults } from "../lib/api";
import { formatInr } from "../lib/accountsSettlement";
import PartyCombobox from "./PartyCombobox";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PCT_PRESETS = [1, 1.5, 2, 2.5, 3];

const DEFAULT_CHARGE_LINES = [
  { code: "commission", label: "Supplier Commission", basis: "GROSS_AMOUNT_PCT", rate: "2.5", effect: "add" },
  { code: "mandi_cess", label: "Mandi Cess", basis: "GROSS_AMOUNT_PCT", rate: "2", effect: "add" },
  { code: "rdf", label: "RDF", basis: "GROSS_AMOUNT_PCT", rate: "2", effect: "add" },
];

function normalizeBasis(basis) {
  const b = String(basis || "GROSS_AMOUNT_PCT").toUpperCase();
  if (b === "FIXED_PER_ORDER") return "FIXED_PER_VEHICLE";
  if (b === "FIXED_PER_QTL" || b === "GROSS_AMOUNT_PCT") return b;
  // Collapse rarer bases into the two Accounts options for this dialog.
  if (b === "FIXED_PER_BAG" || b === "FIXED_PER_VEHICLE") return "FIXED_PER_QTL";
  return "GROSS_AMOUNT_PCT";
}

function computeChargeAmount({ basis, rate }, { gross, billableQtl }) {
  const r = Number(rate || 0);
  if (!(r > 0)) return 0;
  if (basis === "FIXED_PER_QTL") {
    return Math.round(billableQtl * r * 100) / 100;
  }
  return Math.round(gross * (r / 100) * 100) / 100;
}

function purchaseIsForFactory(purchase) {
  if (purchase?.purchase_type === "FOR_FACTORY") return true;
  return (purchase?.contract_allocations || []).some((row) => row?.purchase_type === "FOR_FACTORY");
}

function defaultChargeLinesFor(purchase) {
  if (purchaseIsForFactory(purchase)) return [];
  return DEFAULT_CHARGE_LINES.map((x) => ({ ...x }));
}

function linesFromSuggested(suggested = [], purchase = null) {
  if (Array.isArray(suggested) && suggested.length) {
    const rows = suggested.map((ln) => ({
      code: ln.code || "misc",
      label: ln.label || ln.code || "Charge",
      basis: normalizeBasis(ln.basis),
      rate: ln.rate != null && ln.rate !== "" ? String(ln.rate) : "",
      effect: ln.effect || "add",
    }));
    if (purchaseIsForFactory(purchase)) return [];
    return rows;
  }
  return defaultChargeLinesFor(purchase);
}

function rateSelectValue(basis, rate) {
  if (basis === "FIXED_PER_QTL") return "per_qtl";
  const n = Number(rate);
  if (PCT_PRESETS.some((p) => Math.abs(p - n) < 0.001)) return String(n);
  return "custom";
}

/**
 * Accounts: assign or change supplier on a truck, then confirm charges.
 */
export default function AssignSupplierDialog({
  open,
  onOpenChange,
  purchase,
  onAssigned,
  existingExpenseLines = null,
}) {
  const isChange = String(purchase?.party_kind || "").toUpperCase() === "SUPPLIER";
  const [suppliers, setSuppliers] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [partyId, setPartyId] = useState("");
  const [partyName, setPartyName] = useState("");
  const [step, setStep] = useState(1); // 1 = pick supplier, 2 = charges
  const [submitting, setSubmitting] = useState(false);
  const [chargeLines, setChargeLines] = useState(DEFAULT_CHARGE_LINES.map((x) => ({ ...x })));
  const [assignedPurchase, setAssignedPurchase] = useState(null);
  // Radix can fire a spurious Dialog close when PartyCombobox (Popover) unmounts on step change.
  const ignoreDismissRef = useRef(false);

  const billableQtl = Number(purchase?.net_weight_qtl || 0);
  const materialRate = Number(purchase?.negotiated_rate || purchase?.rate_per_qtl || 0);
  const gross = Math.round(billableQtl * materialRate * 100) / 100;

  // Reset only when the dialog opens or the truck changes — not when parent
  // refreshes party fields after assign-supplier (that was wiping step 2).
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setAssignedPurchase(null);
    ignoreDismissRef.current = false;
    const startAsChange = String(purchase?.party_kind || "").toUpperCase() === "SUPPLIER";
    const startId = startAsChange && purchase?.party_id ? String(purchase.party_id) : "";
    const startName = startAsChange ? (purchase?.party_name || "") : "";
    setPartyId(startId);
    setPartyName(startName);
    if (Array.isArray(existingExpenseLines) && existingExpenseLines.length) {
      setChargeLines(linesFromSuggested(existingExpenseLines, purchase));
    } else {
      setChargeLines(defaultChargeLinesFor(purchase));
    }
    let cancelled = false;
    setLoadingParties(true);
    fetchAllListResults("/parties", { kind: "SUPPLIER", is_active: true })
      .then((rows) => {
        if (!cancelled) setSuppliers(rows || []);
      })
      .catch(() => {
        if (!cancelled) setSuppliers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingParties(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- existingExpenseLines snapshotted on open
  }, [open, purchase?.id]);

  const requestClose = () => {
    ignoreDismissRef.current = false;
    onOpenChange(false);
  };

  const handleOpenChange = (next) => {
    // Ignore Radix dismiss while assigning / while PartyCombobox Popover unmounts.
    if (!next && (submitting || ignoreDismissRef.current)) return;
    onOpenChange(next);
  };

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => String(s.id) === String(partyId)),
    [suppliers, partyId],
  );

  const updateLine = (index, patch) => {
    setChargeLines((prev) => prev.map((ln, i) => (i === index ? { ...ln, ...patch } : ln)));
  };

  const addLine = () => {
    setChargeLines((prev) => [
      ...prev,
      { code: "misc", label: "Other charge", basis: "GROSS_AMOUNT_PCT", rate: "1", effect: "add" },
    ]);
  };

  const removeLine = (index) => {
    setChargeLines((prev) => prev.filter((_, i) => i !== index));
  };

  const setLineRateMode = (index, mode) => {
    const ln = chargeLines[index];
    if (mode === "per_qtl") {
      updateLine(index, {
        basis: "FIXED_PER_QTL",
        rate: ln.basis === "FIXED_PER_QTL" && ln.rate ? ln.rate : "",
      });
      return;
    }
    if (mode === "custom") {
      updateLine(index, {
        basis: "GROSS_AMOUNT_PCT",
        rate: PCT_PRESETS.some((p) => Math.abs(p - Number(ln.rate)) < 0.001) ? "" : ln.rate,
      });
      return;
    }
    updateLine(index, { basis: "GROSS_AMOUNT_PCT", rate: String(mode) });
  };

  const assignSupplier = async () => {
    if (!partyId) {
      toast.error("Select a supplier");
      return;
    }
    try {
      setSubmitting(true);
      ignoreDismissRef.current = true;
      const r = await api.patch(`/purchases/${purchase.id}/assign-supplier`, {
        party_id: Number(partyId),
        sync_gate_pass: true,
        sync_contracts: true,
      });
      const data = r.data || {};
      setAssignedPurchase(data);
      const keepExisting =
        data.supplier_unchanged
        && Array.isArray(existingExpenseLines)
        && existingExpenseLines.length > 0;
      setChargeLines(
        keepExisting
          ? linesFromSuggested(existingExpenseLines, purchase)
          : linesFromSuggested(data.suggested_party_expense_lines, purchase),
      );
      setStep(2);
      toast.success(
        data.supplier_unchanged
          ? `Edit charges for ${data.party_name || partyName}`
          : `Assigned to supplier ${data.party_name || partyName}`,
      );
      // Keep ignoring dismiss until after the step-1 Popover unmount settles.
      window.setTimeout(() => {
        ignoreDismissRef.current = false;
      }, 300);
    } catch (err) {
      ignoreDismissRef.current = false;
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCharges = () => {
    const lines = chargeLines
      .map((ln) => {
        const basis = normalizeBasis(ln.basis);
        const rate = Number(ln.rate || 0);
        const amount = computeChargeAmount({ basis, rate }, { gross, billableQtl });
        return {
          code: String(ln.code || "misc").trim() || "misc",
          label: String(ln.label || ln.code || "Charge").trim(),
          basis,
          rate,
          amount,
          effect: ln.effect === "subtract" ? "subtract" : "add",
        };
      })
      .filter((ln) => Number(ln.amount) > 0 || Number(ln.rate) > 0);

    onAssigned?.({
      purchase: assignedPurchase || {
        ...purchase,
        party_id: Number(partyId),
        party_name: selectedSupplier?.display_name || partyName,
        party_kind: "SUPPLIER",
      },
      // Keep % / ₹-per-qtl lines even when amount is 0 on a zero-weight draft.
      party_expense_lines: lines.filter((ln) => Number(ln.amount) > 0 || Number(ln.rate) > 0),
    });
    requestClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="assign-supplier-dialog"
        onPointerDownOutside={(e) => {
          if (submitting || step === 2) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (submitting || step === 2) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? (isChange ? "Change supplier" : "Assign to supplier")
              : "Supplier charges"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? (isChange
                ? "Pick a different supplier, or keep the current one and continue to edit commission / other charges."
                : "Gate tagged this truck as a farmer. Pick the real supplier so commission and other charges apply on the material voucher.")
              : "Set commission as % of gross or ₹ per quintal. Amounts recalculate from this truck’s net weight."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">{isChange ? "Current supplier" : "Current party"}</Label>
              <p className="text-sm mt-1">
                {purchase?.party_name || "—"}
                <span className="text-muted-foreground text-xs ml-2">
                  ({purchase?.party_kind || "FARMER"})
                </span>
              </p>
              {purchase?.source_farmer_name ? (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Gate farmer kept: <span className="font-medium text-foreground">{purchase.source_farmer_name}</span>
                </p>
              ) : !isChange ? (
                <p className="text-[10px] text-muted-foreground mt-1">
                  This farmer name will be kept on the purchase for accounts verification after you assign a supplier.
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-xs">{isChange ? "Supplier (change or keep)" : "Supplier"}</Label>
              {loadingParties ? (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading suppliers…
                </p>
              ) : (
                <div className="mt-1">
                  <PartyCombobox
                    parties={suppliers}
                    partyId={partyId}
                    partyName={partyName}
                    createKind="SUPPLIER"
                    placeholder="Search supplier…"
                    testIdPrefix="assign-supplier"
                    onChange={({ partyId: id, partyName: name }) => {
                      setPartyId(id || "");
                      setPartyName(name || "");
                    }}
                    onPartyCreated={(row) => {
                      setSuppliers((prev) => {
                        if (prev.some((p) => String(p.id) === String(row.id))) return prev;
                        return [...prev, row];
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1" data-testid="assign-supplier-charges">
            <p className="text-sm font-medium">
              {assignedPurchase?.party_name || selectedSupplier?.display_name}
            </p>
            {(assignedPurchase?.source_farmer_name || purchase?.source_farmer_name) && (
              <p className="text-xs text-muted-foreground" data-testid="assign-supplier-conserved-farmer">
                Gate farmer kept:{" "}
                <span className="font-medium text-foreground">
                  {assignedPurchase?.source_farmer_name || purchase?.source_farmer_name}
                </span>
              </p>
            )}
            <p className="text-[10px] text-muted-foreground">
              Approx. gross {formatInr(gross)} · {billableQtl} qtl · rates from supplier chart (editable).
            </p>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {chargeLines.map((ln, index) => {
                const amount = computeChargeAmount(
                  { basis: ln.basis, rate: ln.rate },
                  { gross, billableQtl },
                );
                const mode = rateSelectValue(ln.basis, ln.rate);
                const showCustomPct = ln.basis === "GROSS_AMOUNT_PCT" && mode === "custom";
                const showPerQtl = ln.basis === "FIXED_PER_QTL";
                return (
                  <div
                    key={`${ln.code}-${index}`}
                    className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <Label className="text-[10px] uppercase text-muted-foreground">
                          {ln.label || ln.code}
                        </Label>
                        <Input
                          className="mt-1 h-8 text-xs"
                          value={ln.label}
                          onChange={(e) => updateLine(index, { label: e.target.value })}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-muted-foreground shrink-0 mt-5"
                        onClick={() => removeLine(index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase text-muted-foreground">Rate</Label>
                        <Select value={mode} onValueChange={(v) => setLineRateMode(index, v)}>
                          <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`assign-supplier-rate-${index}`}>
                            <SelectValue placeholder="Select rate" />
                          </SelectTrigger>
                          <SelectContent>
                            {PCT_PRESETS.map((p) => (
                              <SelectItem key={p} value={String(p)}>
                                {p}% of gross
                              </SelectItem>
                            ))}
                            <SelectItem value="custom">Custom %</SelectItem>
                            <SelectItem value="per_qtl">₹ per quintal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {showCustomPct ? (
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">Custom %</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            className="mt-1 h-8 text-xs"
                            value={ln.rate}
                            placeholder="e.g. 1.75"
                            onChange={(e) => updateLine(index, { rate: e.target.value, basis: "GROSS_AMOUNT_PCT" })}
                          />
                        </div>
                      ) : null}
                      {showPerQtl ? (
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">₹ / qtl</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="mt-1 h-8 text-xs"
                            value={ln.rate}
                            placeholder="e.g. 20"
                            onChange={(e) => updateLine(index, { rate: e.target.value, basis: "FIXED_PER_QTL" })}
                          />
                        </div>
                      ) : null}
                      {!showCustomPct && !showPerQtl ? (
                        <div className="flex items-end">
                          <p className="text-xs text-muted-foreground pb-2 tabular">
                            ≈ {formatInr(amount)}
                          </p>
                        </div>
                      ) : null}
                    </div>
                    {(showCustomPct || showPerQtl) && (
                      <p className="text-[10px] text-muted-foreground tabular">
                        ≈ {formatInr(amount)}
                        {showPerQtl ? ` (${billableQtl} qtl × ₹${Number(ln.rate || 0)})` : ""}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addLine}>
              Add charge line
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step === 2 && (
            <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={submitting}>
              Back
            </Button>
          )}
          <Button type="button" variant="outline" onClick={requestClose} disabled={submitting}>
            Cancel
          </Button>
          {step === 1 ? (
            <Button
              type="button"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={assignSupplier}
              disabled={submitting || !partyId}
              data-testid="assign-supplier-continue"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Continue to charges
            </Button>
          ) : (
            <Button
              type="button"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={confirmCharges}
              data-testid="assign-supplier-confirm-charges"
            >
              {isChange ? "Update charges & continue" : "Apply charges & continue"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
